/**
 * 裝置隔離看板（M2 D-2 / AC-D2-2a8）。
 *
 * 對應 BE GET /admin/device-quarantines/active 與
 * POST /admin/device-quarantines/:device_id/release。
 *
 * Notes：
 *   - 30s 自動重抓（隔離中裝置數為運維核心指標）
 *   - 釋放需 SuperAdmin；released_by_user_id 取自當前登入者（getMe）
 *   - 釋放會寫 audit log、不可復原 → confirm 二次確認
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getMe,
  listActiveQuarantines,
  releaseQuarantine,
  type QuarantineRow,
} from '../lib/api-client';

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('zh-TW');
  } catch {
    return iso;
  }
}

/** quarantined_at 距今多久（粗略 — 分鐘 / 小時 / 天）。 */
function sinceLabel(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return '—';
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min} 分鐘前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小時前`;
  return `${Math.floor(hr / 24)} 天前`;
}

export function QuarantineBoardPage(): JSX.Element {
  const queryClient = useQueryClient();

  const meQuery = useQuery({ queryKey: ['auth.me'], queryFn: getMe });
  const listQuery = useQuery({
    queryKey: ['device-quarantines.active'],
    queryFn: listActiveQuarantines,
    refetchInterval: 30_000,
  });

  const releaseMutation = useMutation({
    mutationFn: (deviceId: string) => {
      const userId = meQuery.data?.user_id;
      if (!userId) {
        return Promise.reject(new Error('無法取得當前使用者 ID（getMe 尚未完成）'));
      }
      return releaseQuarantine(deviceId, userId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['device-quarantines.active'] });
    },
  });

  const rows: QuarantineRow[] = listQuery.data?.rows ?? [];

  const handleRelease = (row: QuarantineRow): void => {
    if (
      window.confirm(
        `確定要釋放此裝置之隔離？\n` +
          `裝置：${row.device_id}\n` +
          `原因：${row.reason}\n\n` +
          `釋放後該裝置可恢復寫入；操作會寫入 audit log，且不可復原。`,
      )
    ) {
      releaseMutation.mutate(row.device_id);
    }
  };

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">裝置隔離看板（D-2 / AC-D2-2a8）</h1>
        <div className="page-head__actions">
          <button onClick={() => listQuery.refetch()} disabled={listQuery.isFetching}>
            {listQuery.isFetching ? '更新中…' : '重新整理'}
          </button>
        </div>
      </div>

      <p style={{ color: '#6b7280' }}>
        裝置收到 ABORT_UNKNOWN / replay attack / 手動隔離後進入 quarantine，停止本機任何寫入。
        本頁列出 active（未釋放）之裝置；釋放限 SuperAdmin。每 30 秒自動更新。
      </p>

      <section
        style={{
          display: 'flex',
          gap: 16,
          margin: '12px 0',
          padding: 12,
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 4,
        }}
      >
        <div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>隔離中裝置數</div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: rows.length > 0 ? '#b91c1c' : '#065f46',
            }}
          >
            {rows.length}
          </div>
        </div>
      </section>

      {listQuery.isLoading && <div>載入中…</div>}
      {listQuery.error && <div className="error-banner">{(listQuery.error as Error).message}</div>}
      {meQuery.error && (
        <div className="error-banner">
          無法取得當前使用者：{(meQuery.error as Error).message}（釋放功能將停用）
        </div>
      )}
      {releaseMutation.error && (
        <div className="error-banner">釋放失敗：{(releaseMutation.error as Error).message}</div>
      )}

      {listQuery.data && (
        <table>
          <thead>
            <tr>
              <th>裝置 ID</th>
              <th>班次</th>
              <th>隔離原因</th>
              <th>隔離時間</th>
              <th style={{ width: 140 }}>動作</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', color: '#6b7280' }}>
                  目前無隔離中裝置
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.id}>
                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.device_id}</td>
                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {row.trip_id ? `${row.trip_id.slice(0, 8)}…` : '—'}
                </td>
                <td>
                  <span className="badge warning">{row.reason}</span>
                </td>
                <td style={{ fontSize: 12, color: '#6b7280' }}>
                  {formatDateTime(row.quarantined_at)}
                  <div>{sinceLabel(row.quarantined_at)}</div>
                </td>
                <td>
                  <button
                    onClick={() => handleRelease(row)}
                    disabled={releaseMutation.isPending || !meQuery.data?.user_id}
                  >
                    釋放隔離
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
